/**
 * "Add several" — paste a list, pick a default occupation, review, add.
 *
 * Thin container: owns the textarea, the default occupation and the run
 * state; parsing/validation/pacing live in lib/bulkAddUsers and each row
 * goes through the SAME per-user pipeline the single Add user drawer uses
 * (`addOne`, injected by Workforce). Rows already on the site, duplicates
 * and malformed lines stay visible in the preview with a reason — the
 * button counts only what will actually be attempted.
 *
 * Runs are sequential with a pause (WorkOS per-org invite rate limit).
 * Closing mid-run stops after the current row; finished rows stay done.
 */
import { useMemo, useRef, useState } from 'react';
import { DrawerShell } from '@/components/Workforce/DrawerShell';
import { GatedButton } from '@/components/GatedButton';
import { BulkAddPreviewTable } from '@/components/Workforce/BulkAddPreviewTable';
import { OccupationSelect } from '@/components/OccupationSelect';
import { SiteRoleSelect } from '@/components/Workforce/SiteRoleSelect';
import type { SiteRole } from '@/lib/siteRole';
import { useOccupations } from '@/hooks/useOccupations';
import type { UserRow } from '@/hooks/useUsers';
import {
  buildBulkRows,
  parseBulkInput,
  runBulkAdd,
  summarizeOutcomes,
  type BulkRow,
  type RowOutcome,
} from '@/lib/bulkAddUsers';

export function BulkAddUsersModal({
  projectId,
  siteName,
  users,
  viewerIsOrgAdmin,
  onCancel,
  addOne,
  onFinished,
}: {
  projectId: string;
  siteName: string;
  /** Roster rows visible to the coordinator; used for "already on site". */
  users: UserRow[];
  /** Gates the site Admin choice (#241). */
  viewerIsOrgAdmin: boolean;
  onCancel: () => void;
  /** The single-user pipeline; must not throw for expected failures. */
  addOne: (input: {
    email: string;
    occupation: string;
    projectId: string;
    siteRole: SiteRole;
  }) => Promise<RowOutcome>;
  /** Called once per run with the summary toast payload. */
  onFinished: (summary: ReturnType<typeof summarizeOutcomes>) => void;
}) {
  const { occupations, isReady: catalogReady } = useOccupations();
  const [text, setText] = useState('');
  const [defaultOccupation, setDefaultOccupation] = useState('');
  const [siteRole, setSiteRole] = useState<SiteRole>('Reader');
  const [outcomes, setOutcomes] = useState<Map<string, RowOutcome>>(new Map());
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const cancelled = useRef(false);

  const onSiteEmails = useMemo(
    () =>
      new Set(
        users
          .filter((u) => projectId !== '' && u.projectIds.includes(projectId))
          .map((u) => u.email.toLowerCase()),
      ),
    [users, projectId],
  );

  const rows: BulkRow[] = useMemo(
    () =>
      buildBulkRows(parseBulkInput(text), {
        defaultOccupation,
        catalog: catalogReady && occupations.length > 0 ? occupations.map((o) => o.title) : null,
        onSiteEmails,
      }),
    [text, defaultOccupation, occupations, catalogReady, onSiteEmails],
  );

  const readyRows = rows.filter((r) => r.problem === null && !outcomes.has(r.key));
  const skipped = rows.length - rows.filter((r) => r.problem === null).length;
  const retryRows = rows.filter((r) => {
    const o = outcomes.get(r.key);
    return r.problem === null && o && (o.kind === 'failed' || o.kind === 'invite-failed');
  });
  const noSite = projectId === '';
  const done = outcomes.size > 0 && readyRows.length === 0 && !running;

  async function run(target: BulkRow[]) {
    if (target.length === 0 || running) return;
    cancelled.current = false;
    setRunning(true);
    const results: RowOutcome[] = [];
    try {
      await runBulkAdd(
        target,
        async (row) => {
          setActiveKey(row.key);
          return addOne({ email: row.email, occupation: row.occupation, projectId, siteRole });
        },
        {
          isCancelled: () => cancelled.current,
          onProgress: (row, outcome) => {
            results.push(outcome);
            setOutcomes((prev) => new Map(prev).set(row.key, outcome));
          },
        },
      );
    } finally {
      setActiveKey(null);
      setRunning(false);
    }
    onFinished(summarizeOutcomes(results));
  }

  function close() {
    cancelled.current = true;
    onCancel();
  }

  const disabledReason = noSite
    ? 'Pick a site in the topbar first'
    : rows.length === 0
      ? 'Paste at least one email'
      : readyRows.length === 0
        ? 'Nothing left to add — fix the flagged lines or close'
        : undefined;

  const footer = (
    <footer
      style={{
        padding: '12px 18px',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ fontSize: 11, color: 'var(--muted)', marginRight: 'auto' }}>
        {rows.length === 0
          ? ''
          : running
            ? `Adding ${outcomes.size + 1} of ${rows.filter((r) => r.problem === null).length}…`
            : `${rows.filter((r) => r.problem === null).length} to add${skipped ? ` · ${skipped} skipped` : ''}`}
      </span>
      <button type="button" className="btn ghost" onClick={close}>
        {running ? 'Stop after this one' : done ? 'Close' : 'Cancel'}
      </button>
      {retryRows.length > 0 && !running ? (
        <button type="button" className="btn" onClick={() => void run(retryRows)}>
          Retry {retryRows.length} failed
        </button>
      ) : null}
      <GatedButton
        className="btn primary"
        busy={running}
        reason={disabledReason ?? null}
        onClick={() => void run(readyRows)}
      >
        {running
          ? 'Working…'
          : `Add ${readyRows.length} user${readyRows.length === 1 ? '' : 's'}`}
      </GatedButton>
    </footer>
  );

  return (
    <DrawerShell
      title="Add several users"
      busy={running}
      canSubmit={false}
      onCancel={close}
      onSubmit={() => {}}
      width={640}
      footer={footer}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
        <span className="label-up">Emails *</span>
        <textarea
          className="inp"
          rows={6}
          value={text}
          disabled={running || outcomes.size > 0}
          onChange={(e) => setText(e.target.value)}
          placeholder={'One person per line:\njane@mine.se\nbob@mine.se, Shift Supervisor\n\nAn occupation after the address overrides the default. Pasting from Outlook or a spreadsheet works too.'}
          spellCheck={false}
        />
      </label>
      <OccupationSelect
        value={defaultOccupation}
        onChange={setDefaultOccupation}
        label="Default occupation"
      />
      <SiteRoleSelect
        value={siteRole}
        onChange={setSiteRole}
        viewerIsOrgAdmin={viewerIsOrgAdmin}
        disabled={running || outcomes.size > 0}
        label="Site role for everyone"
      />
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
        Everyone joins <b>{noSite ? '(no site)' : siteName}</b> as a <b>{siteRole}</b>.
        People already on this site are skipped and keep their role. Each person gets the
        same site access and invite as <b>+ Add user</b>; lines that will not be
        added stay listed below with the reason.
      </div>
      <BulkAddPreviewTable rows={rows} outcomes={outcomes} activeKey={activeKey} />
    </DrawerShell>
  );
}
